/**
 * Judy product mobile interactions.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');

  const getProductScroller = function () {
    return document.querySelector('[data-judy-product-scroller]');
  };

  const getProductScrollTop = function () {
    const scroller = getProductScroller();
    if (scroller instanceof HTMLElement) return scroller.scrollTop;
    return window.scrollY;
  };

  const getProductScrollElement = function () {
    const scroller = getProductScroller();
    return scroller instanceof HTMLElement ? scroller : window;
  };

  const initScrollHeader = function (root) {
    const shell = root.querySelector('.judy-product-mobile__header-shell');
    const header = shell?.querySelector('[data-judy-collection-header]');
    if (!shell || !header || shell.dataset.judyScrollHeaderBound === 'true') return;
    shell.dataset.judyScrollHeaderBound = 'true';

    let ticking = false;

    const syncScrollPadding = function () {
      const headerHeight = shell.offsetHeight;
      document.documentElement.style.setProperty('--judy-product-header-height', headerHeight + 'px');
      const scroller = getProductScroller();
      if (scroller instanceof HTMLElement) scroller.style.scrollPaddingTop = headerHeight + 'px';
    };

    const update = function () {
      ticking = false;
      const gallery = root.querySelector('[data-judy-product-gallery]');
      const solid = gallery instanceof HTMLElement
        ? gallery.getBoundingClientRect().bottom <= (shell.offsetHeight || 0) + 2
        : getProductScrollTop() > 8;
      shell.classList.toggle('is-header-solid', solid);
      root.classList.toggle('is-header-solid', solid);
      header.classList.toggle('is-solid', solid);
      syncScrollPadding();
    };

    const onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    getProductScrollElement().addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    document.addEventListener('cart:update', function () {
      window.requestAnimationFrame(update);
    });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(function () {
        window.requestAnimationFrame(update);
      });
      observer.observe(shell);
      const gallery = root.querySelector('[data-judy-product-gallery]');
      if (gallery instanceof HTMLElement) observer.observe(gallery);
    }
  };

  const initShare = function (root) {
    const shareButton = root.querySelector('[data-judy-product-share]');
    if (!(shareButton instanceof HTMLButtonElement)) return;

    shareButton.addEventListener('click', async function () {
      const url = root.dataset.productUrl || window.location.href;
      const title = root.dataset.productTitle || document.title;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
          return;
        }
        await navigator.clipboard?.writeText(url);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
      }
    });
  };

  const initWishHeart = function (root) {
    const button = root.querySelector('[data-judy-product-wish]');
    if (!(button instanceof HTMLButtonElement)) return;

    button.addEventListener('click', function (event) {
      event.preventDefault();
      const pressed = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', String(!pressed));
      button.classList.toggle('is-active', !pressed);
    });
  };

  const initCollectionToggle = function (root) {
    const section = root.querySelector('[data-judy-product-collection]');
    const toggle = section?.querySelector('[data-judy-collection-toggle]');
    if (!(section instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

    toggle.addEventListener('click', function () {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      section.classList.toggle('is-collapsed', expanded);
    });
  };

  const initFooterDialogNavigation = function (root) {
    const navigation = root.querySelector('.judy-product-mobile__nav');
    if (!(navigation instanceof HTMLElement) || navigation.dataset.judyFooterDialogBound === 'true') return;
    navigation.dataset.judyFooterDialogBound = 'true';

    navigation.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('[data-judy-product-mobile-footer-dialog-key]');
      if (!(link instanceof HTMLAnchorElement) || !navigation.contains(link)) return;

      const dialogKey = link.dataset.judyProductMobileFooterDialogKey;
      if (!dialogKey) return;

      const sharedFooter = document.querySelector('judy-site-desktop-footer.judy-site-desktop-footer--dialog-bridge');
      if (!(sharedFooter instanceof HTMLElement)) return;

      const dialogTrigger = Array.from(sharedFooter.querySelectorAll('[data-judy-footer-dialog-key]')).find(
        (trigger) => trigger.dataset.judyFooterDialogKey === dialogKey
      );
      if (!(dialogTrigger instanceof HTMLButtonElement)) return;

      if (typeof sharedFooter.openFooterDialogForKey !== 'function') return;
      if (sharedFooter.openFooterDialogForKey(dialogKey)) event.preventDefault();
    });
  };

  const initMediaGallery = function (root) {
    const gallery = root.querySelector('[data-judy-product-gallery]');
    if (!(gallery instanceof HTMLElement)) return;

    const tabs = Array.from(gallery.querySelectorAll('[data-judy-media-tab]'));
    const tabList = gallery.querySelector('.judy-product-mobile__media-tabs');
    const panels = Array.from(gallery.querySelectorAll('[data-judy-media-panel]'));
    const showPanel = gallery.querySelector('[data-judy-media-panel="shows"]');
    const showSlideshow = showPanel?.querySelector('slideshow-component');
    const showScroller = showSlideshow?.querySelector('slideshow-slides');
    const showSlides = Array.from(showSlideshow?.querySelectorAll('slideshow-slide.judy-product-mobile__show-slide') || []);
    const allShowsTail = showSlideshow?.querySelector('[data-judy-all-shows-tail]');
    const showInformationLayer = showPanel?.querySelector('[data-judy-show-information-layer]');
    let activeCategory = 'product';
    let activeShowIndex = 0;
    let showSoundMuted = true;
    let galleryVisible = true;
    let tailPreviewing = false;
    let tailGesture = null;
    let tailNavigationStarted = false;
    let tailNavigationTimer = 0;
    let tailScrollEndTimer = 0;
    let categorySwitchTimer = 0;
    let discoveryRevealTimer = 0;
    let discoveryCollapseTimer = 0;
    let discoveryIntroduced = false;
    const tailNavigationDebounceMs = 300;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const collapseDiscovery = function () {
      if (!(tabList instanceof HTMLElement) || activeCategory !== 'product') return;
      tabList.dataset.discoveryState = 'collapsed';
    };

    const revealDiscovery = function () {
      if (!(tabList instanceof HTMLElement) || discoveryIntroduced || !gallery.isConnected) return;
      discoveryIntroduced = true;
      tabList.hidden = false;
      tabList.dataset.discoveryState = 'expanded';
      window.requestAnimationFrame(function () {
        tabList.classList.add('is-discovery-ready');
      });
      window.clearTimeout(discoveryCollapseTimer);
      discoveryCollapseTimer = window.setTimeout(collapseDiscovery, 5000);
    };

    const replaceShowInformation = function (activeIndex) {
      if (!(showInformationLayer instanceof HTMLElement)) return;
      const template = showSlides[activeIndex]?.querySelector('[data-judy-show-information-template]');
      if (!(template instanceof HTMLTemplateElement)) {
        showInformationLayer.replaceChildren();
        showInformationLayer.hidden = true;
        return;
      }

      const nextInformation = template.content.cloneNode(true);
      const currentProfile = showInformationLayer.querySelector('.judy-product-mobile__show-profile');
      const nextProfile = nextInformation.querySelector('.judy-product-mobile__show-profile');

      if (!(currentProfile instanceof HTMLElement) || !(nextProfile instanceof HTMLElement)) {
        showInformationLayer.replaceChildren(nextInformation);
        showInformationLayer.hidden = false;
        return;
      }

      const currentAvatar = currentProfile.querySelector('.judy-product-mobile__show-avatar');
      const nextAvatar = nextProfile.querySelector('.judy-product-mobile__show-avatar');
      const currentAvatarImage = currentAvatar?.querySelector('img');
      const nextAvatarImage = nextAvatar?.querySelector('img');
      const currentAvatarSource = currentAvatarImage?.getAttribute('src') || '';
      const nextAvatarSource = nextAvatarImage?.getAttribute('src') || '';

      if (currentAvatar instanceof HTMLElement && nextAvatar instanceof HTMLElement) {
        if (!currentAvatarSource || currentAvatarSource !== nextAvatarSource) currentAvatar.replaceWith(nextAvatar);
      } else if (currentAvatar instanceof HTMLElement) {
        currentAvatar.remove();
      } else if (nextAvatar instanceof HTMLElement) {
        currentProfile.prepend(nextAvatar);
      }

      const currentProfileCopy = currentProfile.querySelector('.judy-product-mobile__show-profile-copy');
      const nextProfileCopy = nextProfile.querySelector('.judy-product-mobile__show-profile-copy');
      if (currentProfileCopy instanceof HTMLElement && nextProfileCopy instanceof HTMLElement) {
        currentProfileCopy.replaceWith(nextProfileCopy);
      } else if (currentProfileCopy instanceof HTMLElement) {
        currentProfileCopy.remove();
      } else if (nextProfileCopy instanceof HTMLElement) {
        currentProfile.appendChild(nextProfileCopy);
      }

      const currentReview = showInformationLayer.querySelector('.judy-product-mobile__show-review');
      const nextReview = nextInformation.querySelector('.judy-product-mobile__show-review');
      if (currentReview instanceof HTMLElement && nextReview instanceof HTMLElement) {
        currentReview.replaceWith(nextReview);
      } else if (currentReview instanceof HTMLElement) {
        currentReview.remove();
      } else if (nextReview instanceof HTMLElement) {
        showInformationLayer.appendChild(nextReview);
      }

      showInformationLayer.hidden = false;
    };

    const updateSoundButtons = function () {
      gallery.querySelectorAll('[data-judy-show-sound]').forEach(function (button) {
        button.dataset.muted = String(showSoundMuted);
        button.setAttribute('aria-pressed', String(!showSoundMuted));
        button.setAttribute('aria-label', showSoundMuted ? button.dataset.unmuteLabel || '' : button.dataset.muteLabel || '');
      });
    };

    const syncShowVideos = function (activeIndex) {
      showSlides.forEach(function (slide, index) {
        const video = slide.querySelector('video');
        if (!(video instanceof HTMLVideoElement)) return;
        video.loop = true;
        video.muted = showSoundMuted;
        if (
          activeCategory === 'shows'
          && galleryVisible
          && !tailPreviewing
          && index === activeIndex
        ) {
          const promise = video.play();
          if (promise && typeof promise.catch === 'function') promise.catch(function () {});
        } else {
          video.pause();
        }
      });
      updateSoundButtons();
    };

    const tailExposureProgress = function () {
      if (!(showScroller instanceof HTMLElement) || !(allShowsTail instanceof HTMLElement)) return 0;
      const viewportBounds = showScroller.getBoundingClientRect();
      const tailBounds = allShowsTail.getBoundingClientRect();
      if (tailBounds.width <= 0) return 0;
      const visibleWidth = Math.max(
        0,
        Math.min(viewportBounds.right, tailBounds.right) - Math.max(viewportBounds.left, tailBounds.left)
      );
      return Math.min(1, visibleWidth / tailBounds.width);
    };

    const setTailPreviewing = function (previewing) {
      if (tailPreviewing === previewing) return;
      tailPreviewing = previewing;
      if (showInformationLayer instanceof HTMLElement) {
        if (previewing) showInformationLayer.hidden = true;
        else replaceShowInformation(activeShowIndex);
      }
      syncShowVideos(activeShowIndex);
    };

    const alignLastShow = function (smooth) {
      if (!showSlides.length || !(showScroller instanceof HTMLElement)) return;
      const lastIndex = showSlides.length - 1;
      const lastSlide = showSlides[lastIndex];
      activeShowIndex = lastIndex;
      showScroller.scrollTo({
        left: lastSlide.offsetLeft,
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
      });
    };

    const cancelPendingTailNavigation = function () {
      if (!tailNavigationTimer) return false;
      window.clearTimeout(tailNavigationTimer);
      tailNavigationTimer = 0;
      return true;
    };

    const scheduleTailReturn = function () {
      window.clearTimeout(tailScrollEndTimer);
      tailScrollEndTimer = window.setTimeout(function () {
        const remainingProgress = tailExposureProgress();
        if (tailNavigationStarted || tailNavigationTimer || tailGesture || remainingProgress === 0) return;
        alignLastShow(true);
        setTailPreviewing(false);
      }, 100);
    };

    const beginTailGesture = function (clientX, clientY, pointerId) {
      if (cancelPendingTailNavigation()) setTailPreviewing(false);
      const lastIndex = showSlides.length - 1;
      if (
        activeCategory !== 'shows'
        || lastIndex < 0
        || activeShowIndex !== lastIndex
        || tailNavigationStarted
      ) {
        tailGesture = null;
        return;
      }

      window.clearTimeout(tailScrollEndTimer);
      tailGesture = {
        pointerId,
        startX: clientX,
        startY: clientY,
        horizontal: false,
        progress: tailExposureProgress(),
      };
    };

    const updateTailGesture = function (clientX, clientY, pointerId) {
      if (!tailGesture || tailGesture.pointerId !== pointerId) return;
      const deltaX = clientX - tailGesture.startX;
      const deltaY = clientY - tailGesture.startY;

      if (Math.abs(deltaY) > Math.abs(deltaX) + 6) {
        tailGesture = null;
        setTailPreviewing(false);
        return;
      }

      if (deltaX > 8) {
        tailGesture = null;
        setTailPreviewing(false);
        return;
      }

      if (deltaX < -8 && Math.abs(deltaX) > Math.abs(deltaY)) tailGesture.horizontal = true;
      tailGesture.progress = tailExposureProgress();
      if (tailGesture.horizontal && tailGesture.progress > 0) setTailPreviewing(true);
    };

    const finishTailGesture = function (pointerId, cancelled) {
      if (!tailGesture || tailGesture.pointerId !== pointerId) return;
      const gesture = tailGesture;
      tailGesture = null;
      const progress = !cancelled && gesture.horizontal ? tailExposureProgress() : 0;

      if (progress > 0.5 && allShowsTail instanceof HTMLElement && !tailNavigationStarted) {
        const targetUrl = allShowsTail.dataset.judyAllShowsUrl;
        if (targetUrl) {
          window.clearTimeout(tailScrollEndTimer);
          cancelPendingTailNavigation();
          setTailPreviewing(true);
          tailNavigationTimer = window.setTimeout(function () {
            tailNavigationTimer = 0;
            if (tailNavigationStarted || activeCategory !== 'shows') {
              setTailPreviewing(false);
              scheduleTailReturn();
              return;
            }
            tailNavigationStarted = true;
            window.location.assign(targetUrl);
          }, tailNavigationDebounceMs);
          return;
        }
      }

      setTailPreviewing(false);
      scheduleTailReturn();
    };

    if (showScroller instanceof HTMLElement && allShowsTail instanceof HTMLElement) {
      showScroller.addEventListener('scroll', function () {
        const progress = tailExposureProgress();
        if (tailGesture) tailGesture.progress = progress;
        if (tailGesture?.horizontal && progress > 0) setTailPreviewing(true);
        if (progress === 0 && tailPreviewing) setTailPreviewing(false);

        scheduleTailReturn();
      }, { passive: true });

      if ('PointerEvent' in window) {
        showScroller.addEventListener('pointerdown', function (event) {
          if (!event.isPrimary || event.pointerType === 'touch') return;
          beginTailGesture(event.clientX, event.clientY, event.pointerId);
        }, { passive: true });
        showScroller.addEventListener('pointermove', function (event) {
          if (!event.isPrimary || event.pointerType === 'touch') return;
          updateTailGesture(event.clientX, event.clientY, event.pointerId);
        }, { passive: true });
        window.addEventListener('pointerup', function (event) {
          if (!event.isPrimary || event.pointerType === 'touch') return;
          finishTailGesture(event.pointerId, false);
        }, { capture: true, passive: true });
        window.addEventListener('pointercancel', function (event) {
          if (!event.isPrimary || event.pointerType === 'touch') return;
          finishTailGesture(event.pointerId, true);
        }, { capture: true, passive: true });
      }

      showScroller.addEventListener('touchstart', function (event) {
        const touch = event.touches[0];
        if (!touch) return;
        beginTailGesture(touch.clientX, touch.clientY, touch.identifier);
      }, { passive: true });
      showScroller.addEventListener('touchmove', function (event) {
        const touch = Array.from(event.touches).find(function (item) {
          return item.identifier === tailGesture?.pointerId;
        });
        if (!touch) return;
        updateTailGesture(touch.clientX, touch.clientY, touch.identifier);
      }, { passive: true });
      showScroller.addEventListener('touchend', function (event) {
        const touch = Array.from(event.changedTouches).find(function (item) {
          return item.identifier === tailGesture?.pointerId;
        });
        if (!touch) return;
        finishTailGesture(touch.identifier, false);
      }, { passive: true });
      showScroller.addEventListener('touchcancel', function (event) {
        const touch = Array.from(event.changedTouches).find(function (item) {
          return item.identifier === tailGesture?.pointerId;
        });
        if (!touch) return;
        finishTailGesture(touch.identifier, true);
      }, { passive: true });
    }

    showSlideshow?.addEventListener('slideshow:select', function (event) {
      const selectedIndex = Number(event.detail?.index);
      if (!Number.isFinite(selectedIndex)) return;
      const nextShowIndex = Math.max(0, Math.min(selectedIndex, showSlides.length - 1));
      activeShowIndex = nextShowIndex;
      tailPreviewing = false;
      replaceShowInformation(activeShowIndex);
      syncShowVideos(activeShowIndex);
    });

    gallery.querySelectorAll('[data-judy-show-sound]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        showSoundMuted = !showSoundMuted;
        syncShowVideos(activeShowIndex);
      });
    });

    const setCategory = function (category, focusTab) {
      const categoryChanged = activeCategory !== category;
      if (category === 'shows') {
        window.clearTimeout(discoveryCollapseTimer);
        if (tabList instanceof HTMLElement) tabList.dataset.discoveryState = 'collapsed';
      } else if (categoryChanged && discoveryIntroduced && tabList instanceof HTMLElement) {
        tabList.dataset.discoveryState = 'collapsed';
      }
      if (category !== 'shows') {
        cancelPendingTailNavigation();
        tailGesture = null;
        window.clearTimeout(tailScrollEndTimer);
        if (tailExposureProgress() > 0) alignLastShow(false);
        setTailPreviewing(false);
      }
      if (tabList instanceof HTMLElement) {
        window.clearTimeout(categorySwitchTimer);
        if (categoryChanged && !reduceMotion) {
          tabList.classList.add('is-switching');
          categorySwitchTimer = window.setTimeout(function () {
            tabList.classList.remove('is-switching');
          }, 300);
        } else {
          tabList.classList.remove('is-switching');
        }
        tabList.dataset.activeCategory = category;
      }
      activeCategory = category;
      tabs.forEach(function (tab) {
        const selected = tab.dataset.judyMediaTab === category;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? -1 : 0;
        if (!selected && focusTab) tab.focus();
      });
      panels.forEach(function (panel) {
        panel.hidden = panel.dataset.judyMediaPanel !== category;
      });
      syncShowVideos(activeShowIndex);
    };

    tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function (event) {
        setCategory(tab.dataset.judyMediaTab || 'product', event.detail === 0);
      });
      tab.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        setCategory(tabs[nextIndex].dataset.judyMediaTab || 'product', true);
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function (entries) {
        galleryVisible = Boolean(entries[0]?.isIntersecting);
        syncShowVideos(activeShowIndex);
      }, { threshold: 0.05 });
      observer.observe(gallery);
    }

    setCategory('product', false);
    replaceShowInformation(activeShowIndex);
    syncShowVideos(activeShowIndex);
    if (tabList instanceof HTMLElement) {
      window.clearTimeout(discoveryRevealTimer);
      discoveryRevealTimer = window.setTimeout(revealDiscovery, reduceMotion ? 0 : 650);
    }
  };

  const initAbout = function (root) {
    const details = root.querySelector('[data-judy-mobile-about]');
    const summary = details?.querySelector(':scope > summary');
    const content = details?.querySelector('[data-judy-mobile-about-content]');
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    summary.addEventListener('click', function (event) {
      event.preventDefault();
      if (details.dataset.animating === 'true') return;
      const opening = !details.open;
      if (reduceMotion || typeof content.animate !== 'function') {
        details.open = opening;
        return;
      }

      details.dataset.animating = 'true';
      if (opening) details.open = true;
      const height = content.scrollHeight + 'px';
      const animation = content.animate(
        opening
          ? [{ height: '0px', opacity: 0 }, { height, opacity: 1 }]
          : [{ height, opacity: 1 }, { height: '0px', opacity: 0 }],
        { duration: 240, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      animation.addEventListener('finish', function () {
        if (!opening) details.open = false;
        delete details.dataset.animating;
      }, { once: true });
    });
  };

  const initMultiVariantSelector = function (root) {
    const data = root.querySelector('[data-judy-mobile-variants]');
    if (!(data instanceof HTMLScriptElement)) return;

    let variants = [];
    try {
      variants = JSON.parse(data.textContent || '[]');
    } catch (_) {
      variants = [];
    }
    if (!variants.length) return;

    const colorIndex = Number(root.dataset.colorOptionIndex);
    const packagingIndex = Number(root.dataset.packagingOptionIndex);
    const quantities = new Map();
    let activeVariantId = '';
    let busy = false;
    let packagingUnavailable = false;

    const initialVariant = function () {
      const id = String(root.dataset.currentVariantId || '');
      return variants.find(function (variant) { return String(variant.id) === id; })
        || variants.find(function (variant) { return variant.available; })
        || variants[0];
    };

    const optionValue = function (variant, index, fallback) {
      if (!variant || index < 0) return fallback;
      return variant.options?.[index] ?? fallback;
    };

    const firstVariant = initialVariant();
    let currentColor = optionValue(firstVariant, colorIndex, '__default');
    let currentPackaging = optionValue(firstVariant, packagingIndex, '');
    activeVariantId = String(firstVariant.id);
    quantities.set(activeVariantId, 1);

    const colorButtons = function () {
      return Array.from(root.querySelectorAll('[data-judy-mobile-color]'));
    };
    const optionButtons = function () {
      return Array.from(root.querySelectorAll('[data-judy-mobile-option-index][data-judy-mobile-option-value]'));
    };
    const packagingButtons = function () {
      return Array.from(root.querySelectorAll('[data-judy-mobile-packaging-value]'));
    };

    const variantForOptions = function (options) {
      return variants.find(function (variant) {
        return variant.options?.length === options.length
          && variant.options.every(function (option, index) { return option === options[index]; });
      });
    };

    const firstAvailableVariant = function (preferredIndex, preferredValue) {
      if (Number.isInteger(preferredIndex) && preferredIndex >= 0 && preferredValue) {
        const matchingVariant = variants.find(function (variant) {
          return variant.available && variant.options?.[preferredIndex] === preferredValue;
        });
        if (matchingVariant) return matchingVariant;
      }
      return variants.find(function (variant) { return variant.available; }) || variants[0];
    };

    const activeVariant = function () {
      return variants.find(function (variant) { return String(variant.id) === String(activeVariantId); }) || initialVariant();
    };

    const selectVariant = function (variant) {
      if (!variant?.available) return false;
      activeVariantId = String(variant.id);
      currentColor = optionValue(variant, colorIndex, '__default');
      currentPackaging = optionValue(variant, packagingIndex, '');
      quantities.clear();
      quantities.set(activeVariantId, 1);
      packagingUnavailable = false;
      return true;
    };

    const variantForOption = function (index, value) {
      const active = activeVariant();
      if (!active) return undefined;
      const options = [...active.options];
      options[index] = value;
      const exact = variantForOptions(options);
      if (exact?.available) return exact;
      return firstAvailableVariant(index, value);
    };

    const refreshPackagingAvailability = function () {
      packagingUnavailable = false;
    };

    const selectedItems = function () {
      const active = activeVariant();
      const quantity = active ? quantities.get(String(active.id)) || 0 : 0;
      if (!active?.available || quantity <= 0) return [];
      return [{ id: Number(active.id), quantity }];
    };

    const setStatus = function (message) {
      const status = root.querySelector('[data-judy-mobile-status]');
      if (status) status.textContent = message || '';
    };

    const update = function () {
      const active = activeVariant();
      const activeId = active ? String(active.id) : '';

      colorButtons().forEach(function (button) {
        button.setAttribute('aria-pressed', String((button.dataset.judyMobileColor || '__default') === currentColor));
      });

      optionButtons().forEach(function (button) {
        const index = Number(button.dataset.judyMobileOptionIndex);
        const value = button.dataset.judyMobileOptionValue || '';
        const variant = variantForOption(index, value);
        const isActive = Boolean(active && active.options?.[index] === value);
        button.disabled = !variant?.available;
        button.classList.toggle('is-selected', isActive);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
        const count = button.querySelector('[data-judy-mobile-option-count]');
        if (count) {
          const quantity = isActive ? quantities.get(activeId) || 0 : 0;
          count.textContent = quantity > 0 ? String(quantity) : '';
          count.hidden = quantity <= 0;
        }
      });

      packagingButtons().forEach(function (button) {
        const value = button.dataset.judyMobilePackagingValue || '';
        const selected = Boolean(active && active.options?.[packagingIndex] === value);
        const variant = variantForOption(packagingIndex, value);
        button.disabled = !variant?.available;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      const quantity = active ? quantities.get(activeId) || 0 : 0;
      const quantityOutput = root.querySelector('[data-judy-mobile-quantity]');
      if (quantityOutput) quantityOutput.textContent = String(quantity);
      const price = root.querySelector('[data-judy-mobile-price]');
      if (price && active?.price != null) price.textContent = active.price;
      const priceContainer = root.querySelector('[data-judy-mobile-price-container]');
      const comparePrice = root.querySelector('[data-judy-mobile-compare-price]');
      const comparePriceValue = root.querySelector('[data-judy-mobile-compare-price-value]');
      const isDiscounted = Boolean(active?.compareAtPrice);
      if (priceContainer) priceContainer.classList.toggle('is-discounted', isDiscounted);
      if (comparePrice) comparePrice.hidden = !isDiscounted;
      if (comparePriceValue && isDiscounted) comparePriceValue.textContent = active.compareAtPrice;

      root.dataset.packagingUnavailable = String(packagingUnavailable);
      const disabled = selectedItems().length === 0 || busy || packagingUnavailable;
      const addButton = root.querySelector('[data-judy-mobile-add-to-cart]');
      if (addButton) addButton.disabled = disabled;
      root.querySelectorAll('[data-judy-mobile-quantity-change]').forEach(function (button) {
        button.disabled = busy || !active?.available;
      });

      if (packagingUnavailable && !busy) setStatus(root.dataset.packagingUnavailableMessage || root.dataset.errorMessage || '');
      if (activeId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', activeId);
        history.replaceState({}, '', url.toString());
      }
    };

    const setBusy = function (value) {
      busy = value;
      root.dataset.busy = String(value);
      update();
    };

    const setPackaging = function (packaging) {
      if (packagingIndex < 0 || !packaging) return;
      const variant = variantForOption(packagingIndex, packaging);
      if (!selectVariant(variant)) return;
      update();
    };

    const changeQuantity = function (delta) {
      if (busy) return;
      const variant = activeVariant();
      if (!variant?.available) return;
      const id = String(variant.id);
      const current = quantities.get(id) || 0;
      const maximum = Number.isFinite(Number(variant.max)) && Number(variant.max) > 0 ? Number(variant.max) : 999;
      quantities.set(id, Math.max(0, Math.min(maximum, current + delta)));
      refreshPackagingAvailability();
      update();
    };

    const addToCart = async function () {
      const items = selectedItems();
      if (!items.length || busy || packagingUnavailable) return;
      setBusy(true);
      setStatus('');
      try {
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map(function (element) { return element.dataset.sectionId; })
          .filter(Boolean);
        const payload = { items };
        if (sectionIds.length) {
          payload.sections = sectionIds.join(',');
          payload.sections_url = window.location.pathname;
        }
        const response = await fetch(root.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || root.dataset.errorMessage || '');
        const cartResponse = await fetch(root.dataset.cartUrl || '/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await cartResponse.json();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: root.id,
            data: {
              source: 'judy-product-mobile',
              productId: root.dataset.productId,
              itemCount: items.reduce(function (sum, item) { return sum + item.quantity; }, 0),
              sections: result.sections,
            },
          },
        }));
        setStatus(root.dataset.addedMessage || '');
      } catch (error) {
        setStatus(error instanceof Error ? error.message : root.dataset.errorMessage || '');
      } finally {
        setBusy(false);
      }
    };

    const buyNow = async function () {
      const items = selectedItems();
      if (!items.length || busy || packagingUnavailable) return;
      setBusy(true);
      setStatus('');
      try {
        if (!window.JudyBuyNowCheckout?.createCheckout) throw new Error('Unable to create checkout.');
        const checkoutUrl = await window.JudyBuyNowCheckout.createCheckout({
          endpoint: root.dataset.storefrontApiUrl,
          items,
          countryCode: root.dataset.countryCode,
          languageCode: root.dataset.languageCode,
        });
        window.location.assign(checkoutUrl);
      } catch (error) {
        setStatus(error instanceof Error ? error.message : root.dataset.errorMessage || '');
        setBusy(false);
      }
    };

    colorButtons().forEach(function (button) {
      button.addEventListener('click', function () {
        const variant = firstAvailableVariant(colorIndex, button.dataset.judyMobileColor || '__default');
        if (!selectVariant(variant)) return;
        update();
      });
    });

    optionButtons().forEach(function (button) {
      button.addEventListener('click', function () {
        const index = Number(button.dataset.judyMobileOptionIndex);
        const value = button.dataset.judyMobileOptionValue || '';
        const variant = variantForOption(index, value);
        if (!selectVariant(variant)) return;
        update();
      });
    });

    packagingButtons().forEach(function (button) {
      button.addEventListener('click', function () {
        setPackaging(button.dataset.judyMobilePackagingValue || '');
      });
    });
    root.querySelectorAll('[data-judy-mobile-quantity-change]').forEach(function (button) {
      button.addEventListener('click', function () {
        changeQuantity(Number(button.dataset.judyMobileQuantityChange));
      });
    });
    root.querySelector('[data-judy-mobile-add-to-cart]')?.addEventListener('click', addToCart);

    refreshPackagingAvailability();
    update();
  };

  const initRoot = function (root) {
    if (root.dataset.judyProductMobileInit === 'true') return;
    root.dataset.judyProductMobileInit = 'true';
    initScrollHeader(root);
    initShare(root);
    initWishHeart(root);
    initCollectionToggle(root);
    initFooterDialogNavigation(root);
    initMediaGallery(root);
    initAbout(root);
    initMultiVariantSelector(root);
  };

  const boot = function () {
    if (!MOBILE_MQ.matches) return;
    document.querySelectorAll('[data-judy-product-mobile]').forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  if (typeof MOBILE_MQ.addEventListener === 'function') MOBILE_MQ.addEventListener('change', boot);
  document.addEventListener('shopify:section:load', function (event) {
    if (!MOBILE_MQ.matches || !(event.target instanceof HTMLElement)) return;
    const root = event.target.querySelector('[data-judy-product-mobile]');
    if (root) initRoot(root);
  });
  window.addEventListener('pageshow', function () {
    if (MOBILE_MQ.matches && document.body.classList.contains('judy-product-mobile-template')) boot();
  });
})();
